import React, { useState } from "react";
import styled from "styled-components";
import { FaPlus, FaMinus, FaShoppingBasket } from "react-icons/fa";
import { useCart } from "../../subjects/CartContext";
import { thousands_separators } from "../../utils/formatCurrency";
import { getProductImage, ACCENT, TEAL, INK, MUTED, LINE } from "../products/Products";

// Same breakpoint Express.jsx uses for its layout switch — kept as a
// local constant since this component doesn't import from that file.
const DESKTOP_BP = "1024px";

// Minimum sellable amount and the +/- step, in the product's own unit
// (e.g. kg for weighed produce, whole units otherwise). 0.25 lets staff
// ring up part-quantities like 1.25 kg of tilapia for a walk-in customer.
const QTY_STEP = 0.25;
const QTY_MIN = 0.1;
// Round to 2dp so repeated +/- clicks or float math (0.1 + 0.2, etc.)
// never leaves quantities like 1.3000000000000003 in the cart/order payload.
const round2 = (n) => Math.round(n * 100) / 100;

const ExpressProductCard = ({ product }) => {
  const { items, addItemToCart } = useCart();
  const inCart = items?.find((p) => p?.product.id === product.id);
  const [qty, setQty] = useState(inCart?.qty || 1);
  const outOfStock = product.quantity_available_for_sale <= 0;
  const hasDiscount = !!product.discount_amount;
  const image = product?.product_images?.length ? product.product_images[0].url : getProductImage(product.display_name);

  const clamp = (n) =>
    round2(Math.max(QTY_MIN, Math.min(n, product.quantity_available_for_sale || QTY_MIN)));

  const handleQtyInput = (e) => {
    const raw = e.target.value;
    if (raw === "") return setQty(""); // let them clear the field while typing
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    setQty(n);
  };

  // Only clamp/round once they leave the field, so "1." or "1.5" isn't
  // stomped on mid-keystroke.
  const handleQtyBlur = () => {
    if (qty === "" || Number.isNaN(qty)) return setQty(QTY_MIN);
    setQty(clamp(qty));
  };

  return (
    <Card $disabled={outOfStock}>
      <ImageWrap>
        <img src={image} alt={product.display_name || product.name} loading="lazy" />
        {hasDiscount && <DiscountBadge>-{Math.round((product.discount_amount / product.unit_value) * 100)}%</DiscountBadge>}
        {outOfStock && <OosBadge>Out of stock</OosBadge>}
        {inCart && (
          <InCartBadge>
            <FaShoppingBasket size={10} /> {round2(inCart.qty)}
          </InCartBadge>
        )}
      </ImageWrap>

      <Body>
        <Name>{product.display_name || product.name}</Name>
        <PriceRow>
          <Price>KSh {thousands_separators(product.discounted_price ?? product.unit_value)}</Price>
          {hasDiscount && <WasPrice>KSh {thousands_separators(product.unit_value)}</WasPrice>}
        </PriceRow>

        {!outOfStock && (
          <Footer>
            <Stepper>
              <StepBtn onClick={() => setQty((q) => clamp((typeof q === "number" ? q : QTY_MIN) - QTY_STEP))} disabled={qty <= QTY_MIN}>
                <FaMinus size={9} />
              </StepBtn>
              <QtyInput
                type="number"
                inputMode="decimal"
                step="0.01"
                min={QTY_MIN}
                max={product.quantity_available_for_sale || undefined}
                value={qty}
                onChange={handleQtyInput}
                onBlur={handleQtyBlur}
                onClick={(e) => e.stopPropagation()}
              />
              <StepBtn onClick={() => setQty((q) => clamp((typeof q === "number" ? q : QTY_MIN) + QTY_STEP))} disabled={qty >= (product.quantity_available_for_sale || QTY_MIN)}>
                <FaPlus size={9} />
              </StepBtn>
            </Stepper>
            <AddBtn onClick={() => addItemToCart(product, clamp(qty === "" ? QTY_MIN : qty))}>
              <FaShoppingBasket size={12} />
              {inCart ? "Update" : "Add"}
            </AddBtn>
          </Footer>
        )}
      </Body>
    </Card>
  );
};

export default ExpressProductCard;

const Card = styled.div`
  background: #fff;
  border-radius: 1.1rem;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(36, 28, 64, 0.07);
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  display: flex;
  flex-direction: column;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  @media (hover: hover) {
    &:hover {
      transform: ${(p) => (p.$disabled ? "none" : "translateY(-3px)")};
      box-shadow: ${(p) =>
        p.$disabled ? "0 6px 18px rgba(36, 28, 64, 0.07)" : "0 10px 26px rgba(36, 28, 64, 0.12)"};
    }
  }
`;
const ImageWrap = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  background: linear-gradient(180deg, #f8f7fc 0%, #f1f0f8 100%);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;
const DiscountBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #ef4444;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
`;
const OosBadge = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(36, 28, 64, 0.55);
  color: #fff;
  font-weight: 800;
  font-size: 0.75rem;
`;
const InCartBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: ${TEAL};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
`;
const Body = styled.div`
  padding: 0.7rem 0.75rem 0.8rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const Name = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${INK};
  line-height: 1.25;
  min-height: 2.1em;
`;
const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  margin: 0.3rem 0 0.6rem;
`;
const Price = styled.span`
  font-weight: 800;
  font-size: 0.95rem;
  color: ${ACCENT};
`;
const WasPrice = styled.span`
  font-size: 0.72rem;
  color: ${MUTED};
  text-decoration: line-through;
`;
const Footer = styled.div`
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;

  @media (min-width: ${DESKTOP_BP}) {
    gap: 0.5rem;
  }
`;
const Stepper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border: 1.5px solid ${LINE};
  border-radius: 999px;
  padding: 0.15rem;

  @media (min-width: ${DESKTOP_BP}) {
    padding: 0.2rem;
  }
`;
const StepBtn = styled.button`
  width: 22px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f6f5fc;
  color: ${ACCENT};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  @media (min-width: ${DESKTOP_BP}) {
    width: 26px;
    height: 26px;

    @media (hover: hover) {
      &:not(:disabled):hover {
        background: ${ACCENT}22;
      }
    }
  }
`;
const QtyInput = styled.input`
  width: 60px;
  min-width: 84px;
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  color: ${INK};
  border: none;
  background: transparent;
  /* hide the native spinner so it doesn't fight with our +/- buttons */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (min-width: ${DESKTOP_BP}) {
    width: 60px;
    font-size: 1rem;
  }
`;
const AddBtn = styled.button`
  flex: 1 1 84px;
  min-width: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  background: ${ACCENT};
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 0.45rem 0.5rem;
  font-weight: 700;
  font-size: 0.72rem;
  cursor: pointer;
  white-space: nowrap;

  @media (min-width: ${DESKTOP_BP}) {
    padding: 0.5rem 0.6rem;
    font-size: 0.76rem;

    @media (hover: hover) {
      &:hover {
        background: #3d3878;
      }
    }
  }
`;